import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import useUpdateUserProfile from '@/hooks/user/useUpdateUserProfile';
import { populateAddressFormIfExists } from '@/utils/address';
import { useAddressForm } from '@montugroup/circuit-shared';
import useUserProfileData, {
  QUERY_KEY_USER_PROFILE_DATA,
} from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { Logger } from '@montugroup/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const logger = new Logger('useShippingAddress');

export const useShippingAddress = () => {
  const [showManualAddressForm, setShowManualAddressForm] = useState(false);
  const shouldBlockExternalTerritoryPostcodes = useFeatureFlag('enable_blocking_external_territories');
  const formMethods = useAddressForm({ shouldBlockExternalTerritoryPostcodes });
  const queryClient = useQueryClient();

  const { data: userData, getUserState } = useUserProfileData();
  const { mutateAsync, isPending: isUpdatingUserProfile } = useUpdateUserProfile();

  useEffect(() => {
    populateAddressFormIfExists(formMethods, userData?.user, getUserState);
  }, [userData, getUserState]);

  const handleAddressUpdate = async (updatedAddress: Address) => {
    setShowManualAddressForm(false);

    const stateId = userData?.states.find((state: { name?: string }) => state.name === updatedAddress.state)?.id;

    const payload = {
      user: {
        address: `${updatedAddress.addressLine1}$$$$${updatedAddress.addressLine2}`,
        city: updatedAddress.suburb,
        state_id: stateId,
        zip_code: updatedAddress.postcode,
      },
    };

    try {
      await mutateAsync(payload);
      logger.info('User address updated successfully');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER_PROFILE_DATA] });
    } catch (error) {
      logger.error('Failed to update the user address', error);
    }
  };

  const handleSelectedAddress = async (addressData: Address) => {
    await handleAddressUpdate(addressData);
    formMethods.setValue('addressLine1', String(addressData.addressLine1));
    formMethods.setValue('addressLine2', String(addressData.addressLine2));
    formMethods.setValue('postcode', String(addressData.postcode));
    formMethods.setValue('state', String(addressData.state));
    formMethods.setValue('suburb', String(addressData.suburb));
  };

  const handleResetAddress = () => {
    formMethods.reset();
    setShowManualAddressForm(false);
  };

  const handleManualAddressClick = () => {
    setShowManualAddressForm(true);
  };

  const handleCancelUpdate = () => {
    setShowManualAddressForm(false);
  };

  return {
    userData,
    isUpdatingUserProfile,
    formMethods,
    showManualAddressForm,
    setShowManualAddressForm,
    handleAddressUpdate,
    handleSelectedAddress,
    handleResetAddress,
    handleManualAddressClick,
    handleCancelUpdate,
  };
};
